/* global Ryft */
import React, { Component } from 'react';
import Axios from 'axios';
import { connect } from "react-redux";
import i18n from '../../../../i18next';
import { Col, Form } from 'reactstrap';
import { Capacitor } from '@capacitor/core';
import { SERVER_URL, RYFT_GOOGLE_PAY_MERCHANT_ID, RYFT_PUBLIC_KEY } from '../../../../utils/Constants';
import { getAllUrlParams } from '../../../../utils/CommonUtils';
import { parentLocalStorage, sendMessageToParentSite } from '../../../../utils/CrossOriginUtils';
import { getRyftAmount, buildRyftPaymentRequest, createRyftPaymentSession, buildRyftError } from '../../../../utils/RyftPaymentUtils';

import {
    placeOrder,
    processingPayment,
    errorProcessingPayment,
    stopLoader,
    sendEmailReport,
    validateCardInput,
    changeAppleGooglePayAvailability,
    clearPaymentCard
} from "../../../../actions/index";

class RyftForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            clientSecret: null,
            paymentSessionId: null,
            manualOrderId: null,
        }
    }


    componentDidMount = () => {
        if (typeof Ryft === 'undefined') return;
        this.initialiseRyft();
    }

    // The current order total as Ryft payment-session data (amount in MINOR units, e.g. 2027 for 20.27).
    // getAmount() is the gift-card-adjusted shoppingCart total, so this reflects EVERY change to the
    // total made on the checkout after the session was created — tip, service charge, promo, quantity,
    // delivery, etc. — not just tips. Used to set the wallet sheet amount at click time (see
    // walletButtonClickHandler in initialiseWallets) so the customer sees the same amount that is charged.
    currentWalletSessionData = () => {
        const minorUnits = Math.round(parseFloat(this.getAmount()) * 100);
        const currency = this.props.selectedRestaurant.currency;
        // Don't override with a malformed session — it makes Google Pay reject the rebuilt request
        // with OR_BIBED_06. selectedRestaurant.currency is a LOWERCASE code (e.g. 'gbp'); Google Pay /
        // ISO 4217 require uppercase ('GBP'), so normalise it here.
        if (!Number.isFinite(minorUnits) || !currency) return null;
        return { amount: minorUnits, currency: currency.toUpperCase() };
    }

    // Up-front init: create the Ryft payment session via the new backend service to
    // obtain the clientSecret + the customer's stored cards, then build the controller
    // and card form. Saved cards are rendered INSIDE the Ryft form (v3 owns selection);
    // the customer can also enter a new card and tick "store card for later use".
    initialiseRyft = async () => {
        const { createController, createCardForm } = Ryft;

        const sessionData = await this.createPaymentSession();
        if (!sessionData) return; // request failed; createPaymentSession already surfaced the error

        const { clientSecret, paymentSessionId, manualOrderId, accountId } = sessionData.ryftAuthorizationDTO;
        // storedCards is the verbatim Ryft customerGetPaymentMethods response: { items: [...] }
        const storedCards = sessionData.storedCards;
        // accountId is the sub-account id (Platform Fee model). May be null; when set, the SDK sends
        // it as the 'Account' header on EVERY Ryft call — both the GET payment-session it issues when
        // the controller is created and the later attemptPayment. Without it those calls 404 because
        // the session lives under the sub-account, not the main account.

        this.setState({ clientSecret, paymentSessionId, manualOrderId });

        // clientSecret is known up-front, so the controller is created with it; attemptPayment()
        // is then called with no args (it uses the controller's clientSecret + the selected card).
        const controllerConfig = {
            publicKey: RYFT_PUBLIC_KEY,
            clientSecret
        };
        if (accountId) {
            controllerConfig.accountId = accountId;
        }
        const controller = createController(controllerConfig);

        // The session is created up-front (here, at mount) with the total at that moment. Anything that
        // changes the total on the checkout afterwards (tip, service charge, promo, quantity, ...) makes
        // it stale. The CARD flow refreshes it itself — doAuthorize calls updatePaymentSession before
        // attemptPayment. The Google/Apple Pay sheets charge DIRECTLY (no doAuthorize), so without this
        // the session would still hold the create-time amount and the manual capture would exceed the
        // authorised amount and fail. beforePaymentAttempt fires before EVERY charge (card + wallet); for
        // the wallet components we refresh the session to the current total here, so the amount CHARGED
        // matches what the customer saw in the sheet (set by walletButtonClickHandler from the same
        // total). Async is allowed here — this runs after the sheet is approved, outside the click
        // gesture window — unlike walletButtonClickHandler, which forbids network calls.
        controller.setBeforePaymentAttemptHandler(async ({ componentName }) => {
            if (componentName !== 'google-pay' && componentName !== 'apple-pay') {
                return { type: 'success' }; // card-form already refreshed the session in doAuthorize
            }
            const authorizeData = await this.updatePaymentSession();
            if (!authorizeData) {
                return { type: 'reject' }; // updatePaymentSession already surfaced the error
            }
            // manualOrderId comes from the authorize response (not the up-front createPaymentSession);
            // stash it so handleWalletResult places the right order.
            const refreshedManualOrderId = authorizeData.ryftAuthorizationDTO && authorizeData.ryftAuthorizationDTO.manualOrderId
                ? authorizeData.ryftAuthorizationDTO.manualOrderId
                : this.state.manualOrderId;
            this.setState({ manualOrderId: refreshedManualOrderId });
            return { type: 'success' };
        });

        this.cardForm = createCardForm(controller, {
            customerPaymentMethods: {
                // Render the customer's saved cards inside the form (verbatim Ryft response).
                details: storedCards,
                // Show the "store card for later use" checkbox for newly entered cards.
                allowStorage: { enabled: true }
            },
            theme: {
                // Saved cards default to 2 columns (side-by-side). On narrow phone screens two
                // tiles overflow and the second is clipped, so stack them in a single column on
                // mobile and keep the 2-column layout on wider screens. Ryft validates theme
                // values as strings, so this must be "1"/"2", not a number.
                savedCardsColumns: window.innerWidth < 576 ? "1" : "2"
            }
        });

        // New-card entry: enable the place-order button when the entered card is valid.
        this.cardForm.on('validationChange', (event) => {
            this.props.validateCardInput(event.isValid);
        });

        // Saved-card selection happens inside the Ryft form; enable the place-order
        // button when a stored card is selected.
        this.cardForm.on('paymentMethodChange', (event) => {
            console.log("Ryft paymentMethodChange:", event);
            this.props.validateCardInput(true);
        });

        // Render the form into the container in render().
        this.cardForm.mount('#ryft-card');

        // Render the Google Pay / Apple Pay buttons into the same form (Ryft's recommended web
        // pattern). They share the same controller/clientSecret as the card form.
        this.initialiseWallets(controller);
    }

    // Google Pay & Apple Pay buttons for WEB / mobile-browser only. The native apps handle wallets
    // through a separate native Apple/Google Pay plugin (in the native build project), not this.
    // Each button auto-renders only on a supporting browser/device, and each is wrapped so that a
    // wallet init failure can never break the card form.
    // Runs the instant the customer taps a wallet button, BEFORE the payment sheet opens. We recompute
    // the cart total here and hand it back as the sheet's payment session, so the amount shown in the
    // Google/Apple Pay sheet is the current total (incl. any tip / service charge / promo change made
    // after the session was created) rather than the create-time amount. This is the only place the
    // sheet amount can be set, and it must be SYNCHRONOUS (no network) — Apple Pay throws if the handler
    // returns a Promise, since iOS needs the sheet created inside the click's call stack. The matching
    // BACKEND amount is refreshed in beforePaymentAttempt so the charge equals what the customer sees.
    walletButtonClickHandler = () => {
        const paymentSession = this.currentWalletSessionData();
        // If we can't compute the amount, approve without an override (falls back to the loaded session).
        return paymentSession ? { type: 'approve', paymentSession } : { type: 'approve' };
    }

    initialiseWallets = (controller) => {
        // The Ryft WEB wallet buttons only work in a real browser. Inside the native app (Capacitor
        // WebView) Google Pay renders but fails on pay with OR_BIBED_15 ("pop-ups blocked… use a
        // browser such as Chrome/Safari"), because the WebView can't open Google Pay's pop-up/redirect.
        // So skip them in the app — native wallets go through the separate native Apple/Google Pay
        // plugin (like Stripe's native path), not this web SDK.
        if (Capacitor.isNativePlatform() || this.props.pwaAppRunning) {
            return;
        }

        const { createGooglePay, createApplePay } = Ryft;
        const { selectedRestaurant, brand } = this.props;

        const googlePayConfig = {
            merchantName: selectedRestaurant.name,
            merchantCountryCode: brand.countryCode,
            // Show the current cart total in the sheet (see walletButtonClickHandler).
            walletButtonClickHandler: this.walletButtonClickHandler,

            // Default Google Pay button is black;
            //theme: { googlePayButtonColor: 'white' },
        };
        // Non-Hosted Google Pay merchant id: production WEB only, empty in sandbox (see Constants).
        if (RYFT_GOOGLE_PAY_MERCHANT_ID) {
            googlePayConfig.merchantIdentifier = RYFT_GOOGLE_PAY_MERCHANT_ID;
        }
        // Google Pay support is broad (incl. Safari), so it isn't pre-gated — but its readiness check
        // is async and the SDK throws when not ready, so setupWallet clears the skeleton on failure.
        this.googlePay = this.setupWallet(() => createGooglePay(controller, googlePayConfig), 'ryft-google-pay');

        // Apple Pay only exists in Safari / on Apple devices. canMakePayments() is a reliable
        // SYNCHRONOUS check, so pre-gate it — on any other browser we don't mount at all, which
        // avoids the loading skeleton getting stuck (Apple Pay needs no merchantIdentifier on web;
        // Ryft hosted handles merchant validation).
        const applePaySupported = typeof window !== 'undefined'
            && window.ApplePaySession
            && typeof window.ApplePaySession.canMakePayments === 'function'
            && window.ApplePaySession.canMakePayments();

        if (applePaySupported) {
            this.applePay = this.setupWallet(
                () => createApplePay(controller, {
                    merchantName: selectedRestaurant.name,
                    merchantCountryCode: brand.countryCode,
                    // Show the current cart total in the sheet (see walletButtonClickHandler).
                    walletButtonClickHandler: this.walletButtonClickHandler,
                }),
                'ryft-apple-pay'
            );
        }
    }

    // Build + mount a wallet button, wiring its result handler. If init fails — config error
    // (sync throw) or the platform can't pay (Google Pay's async readiness check rejects) — clear
    // the container so Ryft's loading skeleton can't sit there stranded. Returns the wallet or null.
    setupWallet = (factory, containerId) => {
        try {
            const wallet = factory();
            wallet.on('paymentSessionResult', (event) => this.handleWalletResult(event.result));
            const mountResult = wallet.mount(`#${containerId}`);
            if (mountResult && typeof mountResult.catch === 'function') {
                mountResult.catch((error) => this.clearWallet(containerId, error));
            }
            return wallet;
        } catch (error) {
            this.clearWallet(containerId, error);
            return null;
        }
    }

    clearWallet = (containerId, error) => {
        console.log(`Ryft wallet ${containerId} unavailable:`, error);
        const el = document.getElementById(containerId);
        if (el) el.innerHTML = '';
    }

    // Shared handler for the Google/Apple Pay buttons. The wallet sheet performs the charge itself
    // and emits paymentSessionResult; this mirrors doAuthorize's success/error branching. The session
    // amount is refreshed for the wallet charge by the controller's beforePaymentAttempt handler (set
    // in initialiseRyft), so the manualOrderId/amount read here reflect the current total incl. tip.
    handleWalletResult = (result) => {
        console.log('Ryft wallet paymentSessionResult:', result);
        const paymentSession = result.paymentSession;
        const { paymentSessionId, manualOrderId } = this.state;

        if (paymentSession.status === 'Approved' || paymentSession.status === 'Captured') {
            this.props.processingPayment();
            const providerData = { paymentSessionId, amount: this.getAmount(), manualOrderId };
            this.props.placeOrder(false, providerData);
            return;
        }

        if (paymentSession.lastError) {
            this.props.errorProcessingPayment(result.userFacingErrorMessage, 'Payment Failure', this.props.paymentProvider.providerType.id);
        } else {
            this.props.errorProcessingPayment('Payment declined', 'Payment Failure', this.props.paymentProvider.providerType.id);
        }
        // The wallet charge shares this session — rebuild only if it's now terminal (see helper).
        this.reinitialiseRyftIfSessionTerminal(paymentSession);
    }

    // Whether the Ryft payment session is terminal (non-retryable). NOTE: the Ryft API never returns
    // status 'Failed' — the dashboard "FAILED" label just means the session carries a lastError, and
    // per Ryft any session with a lastError is treated as failed/dead (a hard 3DS "Cancel", for
    // example, sets lastError '3ds_authentication_failure' while status stays 'PendingPayment', yet
    // any retry on that clientSecret 500s). So we treat ANY lastError as terminal and rebuild — this
    // covers every failure code, not just 3DS, and guarantees we never retry against a dead session.
    isSessionTerminal = (paymentSession) =>
        !!paymentSession && !!paymentSession.lastError;

    // Rebuild the whole Ryft flow when the session is terminal (see isSessionTerminal).
    reinitialiseRyftIfSessionTerminal = async (paymentSession) => {
        if (this.isSessionTerminal(paymentSession)) {
            await this.reinitialiseRyft();
        }
    }

    // Tear the current form/wallets down and build a brand-new session + controller + form. The
    // newly-entered card cannot be carried over (it lives in Ryft's iframe and the SDK has no way to
    // swap the clientSecret on a mounted form), so this is reserved for the terminal-session case
    // where retrying on the old session is impossible anyway.
    reinitialiseRyft = async () => {
        console.log("Re-initialising Ryft after a failed payment (session may be terminal).");

        // Drop the dead form/wallets so the next doAuthorize uses the fresh ones built by initialiseRyft.
        // The SDK card form exposes unmount(); fall back to destroy() if present on this version.
        try {
            if (this.cardForm && typeof this.cardForm.unmount === 'function') {
                this.cardForm.unmount();
            } else if (this.cardForm && typeof this.cardForm.destroy === 'function') {
                this.cardForm.destroy();
            }
        } catch (e) {
            console.log("Ryft cardForm teardown failed (ignored):", e);
        }
        this.cardForm = null;
        this.googlePay = null;
        this.applePay = null;

        // Clear the SDK's mounted iframes so the re-mount in initialiseRyft doesn't duplicate them.
        ['ryft-card', 'ryft-google-pay', 'ryft-apple-pay'].forEach((id) => {
            const el = document.getElementById(id);
            if (el) el.innerHTML = '';
        });

        // The rebuilt form starts with nothing selected — disable the place-order button until the
        // customer re-selects a saved card or re-enters a new one.
        this.props.validateCardInput(false);

        await this.initialiseRyft();
    }

    // Current order total (uses the gift-card-adjusted total when present). Shared logic in RyftPaymentUtils.
    getAmount = () => getRyftAmount(this.props.shoppingCart, this.props.giftCards);

    // Shared request body for createPaymentSession (init) and /payment/authorize (update).
    buildPaymentRequest = () => buildRyftPaymentRequest({
        customer: this.props.customer,
        selectedRestaurant: this.props.selectedRestaurant,
        shoppingCart: this.props.shoppingCart,
        giftCards: this.props.giftCards,
    });

    // New Ryft-only backend service: creates the payment session and returns the
    // clientSecret + the customer's stored cards (verbatim Ryft response).
    createPaymentSession = async () => {
        const { customer, selectedRestaurant } = this.props;

        try {
            const data = await createRyftPaymentSession(this.buildPaymentRequest(), customer.token);
            console.log("Ryft createPaymentSession response:", data);
            return data;
        } catch (error) {
            // Surface the friendly userMessage from the backend DTO, falling back to the generic
            // Axios message ("Request failed with status code 500") — never the raw stack trace.
            this.props.errorProcessingPayment(buildRyftError(error), 'Authorize Failure', this.props.paymentProvider.providerType.id);
            this.props.sendEmailReport(selectedRestaurant.restaurantId, error, 'Ryft createPaymentSession error');
            return null;
        }
    }

    doAuthorize = async (e) => {
        e.preventDefault();
        console.log("Ryft DoAuthorize started");

        this.props.processingPayment();

        const { clientSecret, paymentSessionId } = this.state;

        if (!clientSecret || !this.cardForm) {
            // The session/form wasn't initialised (createPaymentSession failed or SDK missing).
            this.props.errorProcessingPayment('Payment not ready', 'Payment Failure', this.props.paymentProvider.providerType.id);
            return;
        }

        // 1. Update the payment session FIRST, so the amount the customer authorises is
        //    current (e.g. a tip added after the session was created). The backend updates
        //    the session, prepares the manual capture and returns the manualOrderId to place.
        const authorizeData = await this.updatePaymentSession();
        if (!authorizeData) return; // error already surfaced by updatePaymentSession

        // The manualOrderId comes from the authorize response (not the up-front
        // createPaymentSession), so read it from there for the placeOrder request.
        const manualOrderId = authorizeData.ryftAuthorizationDTO && authorizeData.ryftAuthorizationDTO.manualOrderId
            ? authorizeData.ryftAuthorizationDTO.manualOrderId
            : this.state.manualOrderId;

        // 2. stop the transparent loader before attemptPayment
        //    so the Ryft 3DS challenge (if any) is visible and interactive instead of sitting behind
        //    them.
        this.props.stopLoader();

        try {
            // 3. Customer authorises the up-to-date amount via the Ryft form. attemptPayment
            //    takes no args (clientSecret is on the controller); it charges the selected
            //    saved card OR the newly entered one. 3DS / required actions handled internally.
            console.log("Attempting Ryft payment for session:", paymentSessionId);
            const result = await this.cardForm.attemptPayment();
            console.log("Ryft attemptPayment result:", result);

            const paymentSession = result.paymentSession;

            if (paymentSession.status === 'Approved' || paymentSession.status === 'Captured') {
                console.log("Payment authorised:", paymentSession);
                // 4. Place the order; the backend proceeds to manual capture.
                this.props.processingPayment();
                const providerData = { paymentSessionId, amount: this.getAmount(), manualOrderId };
                this.props.placeOrder(false, providerData);
            } else if (paymentSession.lastError) {
                console.log("Ryft lastError:", paymentSession.lastError, "status:", paymentSession.status, "userFacingError:", result.userFacingErrorMessage);
                this.props.errorProcessingPayment(result.userFacingErrorMessage, 'Payment Failure', this.props.paymentProvider.providerType.id);
                await this.reinitialiseRyftIfSessionTerminal(paymentSession);
            } else {
                console.log("Ryft unhandled status:", paymentSession.status, paymentSession);
                this.props.errorProcessingPayment('Payment declined', 'Payment Failure', this.props.paymentProvider.providerType.id);
                await this.reinitialiseRyftIfSessionTerminal(paymentSession);
            }
        } catch (error) {
            // A thrown error means we never got a paymentSession to inspect. Leave the form (and the
            // customer's entered card) intact so they can retry — if the session turns out to be dead,
            // the next attempt resolves with status 'Failed' and we rebuild then.
            console.error("Payment failed:", error);
            this.props.errorProcessingPayment(error, 'Payment Failure', this.props.paymentProvider.providerType.id);
        }
    }

    // Update the payment session on the backend before the customer authorises it.
    // Request shape is unchanged from the original /payment/authorize call.
    // Returns the response data (which carries the manualOrderId) on success, false on failure.
    updatePaymentSession = async () => {
        const { customer, selectedRestaurant } = this.props;
        const { paymentSessionId } = this.state;
        const header = { headers: { 'Authorization': `TOKEN${customer.token}` } };

        const request = this.buildPaymentRequest();
        request.ryftAuthorizeRequest.paymentSessionId = paymentSessionId;

        try {
            const response = await Axios.post(`${SERVER_URL}/payment/authorize`, request, header);
            console.log("Ryft authorization (session update) response", response);
            return response.data;
        } catch (error) {
            // Surface the friendly userMessage from the backend DTO, falling back to the generic
            // Axios message ("Request failed with status code 500") — never the raw stack trace.
            this.props.errorProcessingPayment(buildRyftError(error), 'Authorize Failure', this.props.paymentProvider.providerType.id);
            this.props.sendEmailReport(selectedRestaurant.restaurantId, error, 'Ryft authorize error');
            return false;
        }
    }

    render(){
        const { ryftElementContainer } = styles;

        return (
            <Col xs={12} sm={12} md={8} style={ryftElementContainer} hidden={this.props.paymentWithCard}>
                <Form id="ryft-pay-form" className="Ryft--payform" onSubmit={this.doAuthorize}>
                    {/* Wallet buttons render only on supporting browsers/devices (web only). */}
                    <div id="ryft-apple-pay"></div>
                    <div id="ryft-google-pay"></div>
                    <div id="ryft-card"></div>
                    <div id="ryft-pay-error"></div>
                </Form>
            </Col>
        )
    }
}

const mapStateToProps = state => {
    console.log("state:", state);
    return {
        brand: state.brand,
        customer: state.customer,
        payment: state.payment,
        selectedRestaurant: state.selectedRestaurant,
        orderTypes: state.orderTypes,
        shoppingCart: state.shoppingCart,
        menuPreviewMode: state.menuPreviewMode.previewMode,
        customerThemes: state.customerThemes,
        pwaAppRunning: state.pwaAppRunning,
        giftCards: state.giftCards,
        paymentProvider: state.paymentProvider
    };
};

export default connect(mapStateToProps, { placeOrder, processingPayment, errorProcessingPayment, stopLoader, sendEmailReport, validateCardInput, changeAppleGooglePayAvailability, clearPaymentCard })(RyftForm);
// export default RyftForm;

let styles = {
    ryftElementContainer: {
        margin: 'auto',
        paddingTop: 10,
        paddingBottom: 10,

    }
}